import React, { useState, useEffect } from 'react';
import { builderAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Palette, Image, Save, Upload, Check, Building2, Eye
} from 'lucide-react';

const BrandingPage = () => {
  const { builder, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [branding, setBranding] = useState({
    logo_url: '',
    primary_color: '#0F172A',
    secondary_color: '#B45309',
    favicon_url: '',
  });

  const [modules, setModules] = useState([]);

  const allModules = [
    { id: 'lead_registry', name: 'Lead Registry', description: 'Central lead database with duplicate detection' },
    { id: 'client_tagging', name: 'Client Tagging', description: '3-month exclusivity protection' },
    { id: 'partner_lead_management', name: 'Partner Lead Management', description: 'Mini CRM for partners' },
    { id: 'site_visit', name: 'Site Visit Governance', description: 'Mandatory visit registration' },
    { id: 'commission_tracking', name: 'Commission Tracking', description: 'Stage-wise commission management' },
    { id: 'performance_scoring', name: 'Performance Scoring', description: 'Partner ranking and analytics' },
    { id: 'inventory', name: 'Inventory Management', description: 'Real-time unit availability' },
    { id: 'microsite_builder', name: 'Microsite Builder', description: 'Partner website builder' },
    { id: 'co_branding', name: 'Co-Branding', description: 'Brand assets management' },
    { id: 'analytics', name: 'Analytics', description: 'Performance dashboards' },
    { id: 'communication', name: 'Communication', description: 'Chat and notifications' },
    { id: 'payment_communication', name: 'Payment Communication', description: 'Monthly demand notices' },
  ];

  useEffect(() => {
    if (builder) {
      setBranding(builder.branding || {
        logo_url: '',
        primary_color: '#0F172A',
        secondary_color: '#B45309',
        favicon_url: '',
      });
      setModules(builder.enabled_modules || []);
    }
  }, [builder]);

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await builderAPI.update(builder.id, { branding });
      toast.success('Branding updated successfully');
    } catch (error) {
      toast.error('Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveModules = async () => {
    setSaving(true);
    try {
      await builderAPI.update(builder.id, { enabled_modules: modules });
      toast.success('Module settings updated');
    } catch (error) {
      toast.error('Failed to update modules');
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (moduleId) => {
    if (modules.includes(moduleId)) {
      setModules(modules.filter(m => m !== moduleId));
    } else {
      setModules([...modules, moduleId]);
    }
  };

  if (!builder) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="branding-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Branding & Settings</h1>
        <p className="text-muted-foreground">Customize your platform appearance and enable modules</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Branding Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Brand Colors
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Logo URL</Label>
              <div className="flex gap-2">
                <Input 
                  value={branding.logo_url || ''} 
                  onChange={(e) => setBranding({...branding, logo_url: e.target.value})}
                  placeholder="https://your-logo-url.com/logo.png"
                />
                <Button variant="outline" size="icon">
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
              {branding.logo_url && (
                <div className="mt-2 p-4 bg-muted rounded-lg">
                  <img src={branding.logo_url} alt="Logo preview" className="max-h-16 object-contain" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary Color</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={branding.primary_color || '#0F172A'} 
                    onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input 
                    value={branding.primary_color || '#0F172A'} 
                    onChange={(e) => setBranding({...branding, primary_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Secondary Color</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={branding.secondary_color || '#B45309'} 
                    onChange={(e) => setBranding({...branding, secondary_color: e.target.value})}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input 
                    value={branding.secondary_color || '#B45309'} 
                    onChange={(e) => setBranding({...branding, secondary_color: e.target.value})}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Favicon URL</Label>
              <Input 
                value={branding.favicon_url || ''} 
                onChange={(e) => setBranding({...branding, favicon_url: e.target.value})}
                placeholder="https://your-favicon-url.com/favicon.ico"
              />
            </div>

            {/* Preview */}
            <div className="space-y-2">
              <Label>Preview</Label>
              <div 
                className="rounded-lg p-4 text-white"
                style={{ backgroundColor: branding.primary_color }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-6 w-6" style={{ color: branding.secondary_color }} />
                  <span className="font-bold">{builder.name}</span>
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-4 py-2 rounded text-sm font-medium"
                    style={{ backgroundColor: branding.secondary_color }}
                  >
                    Primary Button
                  </button>
                  <button className="px-4 py-2 rounded text-sm font-medium border border-white/30">
                    Secondary Button
                  </button>
                </div>
              </div>
            </div>

            <Button onClick={handleSaveBranding} disabled={saving} className="w-full">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Branding'}
            </Button>
          </CardContent>
        </Card>

        {/* Modules Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Enabled Modules
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enable or disable platform modules. Partners and team members will only see enabled modules.
            </p>
            
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {allModules.map((module) => (
                <div 
                  key={module.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    modules.includes(module.id) 
                      ? 'border-emerald-200 bg-emerald-50/50' 
                      : 'border-border bg-muted/30'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{module.name}</h4>
                      {modules.includes(module.id) && (
                        <Check className="h-4 w-4 text-emerald-600" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{module.description}</p>
                  </div>
                  <Switch 
                    checked={modules.includes(module.id)}
                    onCheckedChange={() => toggleModule(module.id)}
                  />
                </div>
              ))}
            </div>

            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-muted-foreground">
                  {modules.length} of {allModules.length} modules enabled
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setModules(allModules.map(m => m.id))}>
                    Enable All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setModules([])}>
                    Disable All
                  </Button>
                </div>
              </div>
              <Button onClick={handleSaveModules} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Module Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BrandingPage;
